import { useRouterState } from "@tanstack/react-router";
import { getVideoMeta, YT_EMBED, YT_THUMB, YT_WATCH } from "@/content/video-library";
import { BASE_URL, LOCALE, ORG_ID } from "@/lib/seo/constants";

/**
 * VideoObject-schema för varje inbäddad YouTube-film.
 *
 * Renderas direkt i komponenten som visar filmen, så att ALLA sidor med
 * en reel (tjänstesidor, landningssidor, case) automatiskt får markup som
 * Googles videokarusell och Video-fliken kan läsa.
 *
 * transcript och Clip-kapitel läggs bara ut när verklig data finns i
 * videobiblioteket — vi hittar aldrig på tal eller tidskoder.
 */
export function VideoSchema({
  videos,
  path,
}: {
  videos: { videoId: string; title?: string }[];
  /** Sidans sökväg — används för Clip-URL:er (#t=). */
  path?: string;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const currentPath = path ?? pathname;
  if (!videos || videos.length === 0) return null;

  const seen = new Set<string>();
  const nodes = videos
    .filter((v) => v.videoId && !seen.has(v.videoId) && seen.add(v.videoId))
    .map((v) => {
      const meta = getVideoMeta(v.videoId, v.title);
      const pageUrl = currentPath ? `${BASE_URL}${currentPath}` : undefined;
      return {
        "@context": "https://schema.org",
        "@type": "VideoObject",
        "@id": `${BASE_URL}/#video-${meta.id}`,
        name: meta.name,
        description: meta.description,
        thumbnailUrl: [YT_THUMB(meta.id)],
        uploadDate: meta.uploadDate,
        ...(meta.duration ? { duration: meta.duration } : {}),
        embedUrl: YT_EMBED(meta.id),
        contentUrl: YT_WATCH(meta.id),
        ...(pageUrl ? { url: pageUrl } : {}),
        publisher: { "@id": ORG_ID },
        creator: { "@id": ORG_ID },
        productionCompany: { "@id": ORG_ID },
        inLanguage: LOCALE,
        isFamilyFriendly: true,
        ...(meta.client ? { about: meta.client } : {}),
        ...(meta.transcript ? { transcript: meta.transcript } : {}),
        ...(meta.chapters && meta.chapters.length > 0
          ? {
              hasPart: meta.chapters.map((c) => ({
                "@type": "Clip",
                name: c.name,
                startOffset: c.startOffsetSec,
                ...(c.endOffsetSec ? { endOffset: c.endOffsetSec } : {}),
                ...(pageUrl ? { url: `${pageUrl}#t=${c.startOffsetSec}` } : {}),
              })),
            }
          : {}),
      };
    });

  return (
    <>
      {nodes.map((n) => (
        <script
          key={n["@id"]}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(n) }}
        />
      ))}
    </>
  );
}
